import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button
} from '@mui/material';
import { FilterList, Refresh, Star, TrendingUp, People } from '@mui/icons-material';
import { brandColors } from '../../../../theme/theme';

const DeepDiveFilters = ({ filters, onFilterChange, onReset, onPresetApply }) => {
  const presets = [
    {
      id: 'high-value',
      label: 'High Value Audience',
      icon: <Star />,
      filters: { movieFrequency: 'Frequents' }
    },
    {
      id: 'growth-segments',
      label: 'Growth Segments',
      icon: <TrendingUp />,
      filters: { ageGroup: 'Under 35' }
    },
    {
      id: 'diverse-audience',
      label: 'Diverse Audience',
      icon: <People />,
      filters: { ethnicity: '' } // All ethnicities
    }
  ];

  const incomeOptions = [
    { value: '', label: 'All Income Levels' },
    { value: 'Under $25k', label: 'Under $25k' },
    { value: '$25k-$50k', label: '$25k - $50k' },
    { value: '$50k-$75k', label: '$50k - $75k' },
    { value: '$75k-$100k', label: '$75k - $100k' },
    { value: '$100k-$150k', label: '$100k - $150k' },
    { value: '$150k+', label: '$150k+' }
  ];

  const identityOptions = [
    { value: '', label: 'All Identities' },
    { value: 'Straight', label: 'Straight' },
    { value: 'LGBTQI+', label: 'LGBTQI+' },
    { value: 'Other', label: 'Other/Prefer not to say' }
  ];

  const movieFrequencyOptions = [
    { value: '', label: 'All Frequencies' },
    { value: 'Frequents', label: 'Frequent Moviegoers' },
    { value: 'Casual', label: 'Casual Moviegoers' },
    { value: 'Home', label: 'Home Viewers' },
    { value: 'Non-Movie Watchers', label: 'Non-Movie Watchers' }
  ];

  const ageOptions = [
    { value: '', label: 'All Ages' },
    { value: 'Under 35', label: 'Under 35' },
    { value: 'Over 35', label: 'Over 35' }
  ];

  const genderOptions = [
    { value: '', label: 'All Genders' },
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' }
  ];

  const ethnicityOptions = [
    { value: '', label: 'All Ethnicities' },
    { value: 'White', label: 'White' },
    { value: 'Black', label: 'Black' },
    { value: 'Hispanic', label: 'Hispanic' },
    { value: 'Asian/Pacific Islander', label: 'Asian/Pacific Islander' },
    { value: 'Other', label: 'Other' }
  ];

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList sx={{ color: brandColors.primary }} />
          <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600 }}>
            Advanced Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label="Filters Active"
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={onReset}
          startIcon={<Refresh />}
          disabled={!hasActiveFilters}
        >
          Reset All
        </Button>
      </Box>

      {/* Quick Presets */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 600 }}>
          Quick Presets:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {presets.map(preset => (
            <Chip
              key={preset.id}
              icon={preset.icon}
              label={preset.label}
              onClick={() => onPresetApply(preset.filters)}
              clickable
              sx={{
                '&:hover': {
                  backgroundColor: brandColors.primary + '20'
                }
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Filter Controls */}
      <Grid container spacing={2}>
        {/* Movie Frequency */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Movie Frequency</InputLabel>
            <Select
              value={filters.movieFrequency || ''}
              label="Movie Frequency"
              onChange={(e) => onFilterChange('movieFrequency', e.target.value)}
            >
              {movieFrequencyOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Age Group */}
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Age Group</InputLabel>
            <Select
              value={filters.ageGroup || ''}
              label="Age Group"
              onChange={(e) => onFilterChange('ageGroup', e.target.value)}
            >
              {ageOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Gender */}
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Gender</InputLabel>
            <Select
              value={filters.gender || ''}
              label="Gender"
              onChange={(e) => onFilterChange('gender', e.target.value)}
            >
              {genderOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Ethnicity */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Ethnicity</InputLabel>
            <Select
              value={filters.ethnicity || ''}
              label="Ethnicity"
              onChange={(e) => onFilterChange('ethnicity', e.target.value)}
            >
              {ethnicityOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Income (NEW) */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Income Level</InputLabel>
            <Select
              value={filters.income || ''}
              label="Income Level"
              onChange={(e) => onFilterChange('income', e.target.value)}
            >
              {incomeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Identity (NEW) */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Identity</InputLabel>
            <Select
              value={filters.identity || ''}
              label="Identity"
              onChange={(e) => onFilterChange('identity', e.target.value)}
            >
              {identityOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Survey Date Range */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>From Date</InputLabel>
              <Select
                value={filters.surveyDateFrom || ''}
                label="From Date"
                onChange={(e) => onFilterChange('surveyDateFrom', e.target.value)}
                displayEmpty
              >
                <MenuItem value="">All Dates</MenuItem>
                {/* Add date options dynamically based on available survey dates */}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DeepDiveFilters;

